<template>
  <v-dialog
    :model-value="value"
    width="550px"
    height="575px"
    content-class="copy-decisions-modal"
    data-id-e2e="copyDecisionsModal"
    @click:outside="closeModal"
  >
    <BaseDialogCard :title="tkey('modalTitle')" @close="closeModal">
      <v-container fluid class="pa-0">
        <v-row class="d-flex flex-column main-container pt-1 mb-0">
          <v-col class="pl-0 pb-0 pt-0 pr-4 content">
            <v-row class="d-flex align-center justify-center">
              <v-col class="pa-0 ma-0">
                {{ tkey('selectProductsToUpdate') }}
              </v-col>
              <v-col class="pa-0 ma-0 text-end">
                <CopyModellingDecisionsFilterToggle
                  :selected-toggle="selectedToggle"
                  :disable-filtered-products-toggle="isFilteredProductsToggleDisabled"
                  @toggle-selected="onFilterToggleSelected"
                />
              </v-col>
            </v-row>
            <!-- Search scenario section -->
            <v-row>
              <SearchScenario
                block
                data-id-e2e="copyDecisionsSearchScenarioSection"
                :rules="[]"
                :active-scenario-id="selectedScenario._id"
                :scenarios-search-query="scenariosSearchQuery"
                @scenario-id-selected="onScenarioIdChange"
              />
            </v-row>

            <!-- Show progress bar if products data is loading -->
            <div v-if="isLoading" class="d-flex justify-center align-center pt-1">
              <v-progress-circular indeterminate color="primary" />
            </div>

            <div v-else>
              <!-- Matching products indication section -->
              <v-row v-if="matchingProductsInfo.matchingProductCount" class="pt-3">
                {{ tkey('matchingProducts', [matchingProductsInfo.matchingProductCount]) }}
              </v-row>
              <v-row v-if="matchingProductsInfo.nonMatchingProductCount" class="pt-1">
                {{ tkey('nonMatchingProducts', [matchingProductsInfo.nonMatchingProductCount]) }}
              </v-row>
            </div>

            <!-- Decisions selection section -->
            <v-row class="pt-1">
              {{ tkey('decisionsToCopy') }}
            </v-row>
            <v-row class="d-flex align-center pa-0 ma-0">
              <v-checkbox
                id="select-sister-products-decisions"
                :model-value="selectedDecisions.sisterProducts"
                hide-details
                density="compact"
                disabled
                class="rtls-checkbox ml-2 pl-2 mx-2"
              />
              <label for="select-sister-products-decisions">
                {{ $t('productModellingPage.sisterProducts') }}
              </label>
            </v-row>
            <v-row class="d-flex align-center pa-0 ma-0">
              <v-checkbox
                id="select-trend-applied-decisions"
                :model-value="selectedDecisions.trendApplied"
                hide-details
                density="compact"
                class="rtls-checkbox ml-2 pl-2 mx-2"
                @update:model-value="selectedDecisions.trendApplied = $event"
              />
              <label for="select-trend-applied-decisions">
                {{ $t('productModellingPage.trendApplied') }}
              </label>
            </v-row>
            <v-row class="d-flex align-center pa-0 ma-0">
              <v-checkbox
                id="select-ros-override-decisions"
                :model-value="selectedDecisions.rateOfSaleOverride"
                hide-details
                density="compact"
                class="rtls-checkbox ml-2 pl-2 mx-2"
                @update:model-value="selectedDecisions.rateOfSaleOverride = $event"
              />
              <label for="select-ros-override-decisions">
                {{ $t('productModellingPage.salesOverride') }}
              </label>
            </v-row>

            <!-- Info message -->
            <v-container class="pa-2 ma-0">
              <v-alert class="alert--small" variant="tonal" type="warning">
                {{ tkey('infoNewModellingPage') }}
              </v-alert>
            </v-container>
          </v-col>
        </v-row>
      </v-container>

      <!-- Footer -->
      <template #footer>
        <PageActions
          :has-data-errors="hasDataErrors"
          :has-data-changes="true"
          :show-discard="false"
          :save-loading="isProcessing"
          save-btn-text="copyDecisions"
          @save="copyDecisions"
        >
          <template #right-btns>
            <v-btn class="text-outline" variant="text" @click="closeModal">
              {{ $t('actions.cancel') }}
            </v-btn>
          </template>
        </PageActions>
      </template>
    </BaseDialogCard>
  </v-dialog>
</template>

<script>
import { useInternationalization } from '@/composables/use-internationalization';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useScenariosStore } from '@/stores/scenarios';
import jobapi from '@enums/jobapi';
import { filter, isEmpty, keys, size } from 'lodash';
import { mapActions, mapState } from 'pinia';

import toggleOptions from '@sharedModules/data/enums/copy-decisions-modal-toggle-options';

export default {
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    isProcessing: {
      type: Boolean,
      required: false,
      default: false,
    },
    filteredProductDisplayKeys: {
      type: Array,
      required: false,
      default: () => null,
    },
  },

  emits: ['close', 'confirm', 'diff-change'],

  setup() {
    const { tkey } = useInternationalization('productModellingPage.copyDecisionsModal');
    return { tkey };
  },

  data() {
    return {
      isLoading: false,
      sourceScenarioId: null,
      matchingProductsInfo: {},
      selectedDecisions: {
        sisterProducts: true,
        trendApplied: false,
        rateOfSaleOverride: false,
      },
      toggleValue: toggleOptions.allProducts,
    };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useScenarioProductsStore, ['loading']),

    hasDataErrors() {
      return this.isLoading || !this.isMatchingProductsDataLoaded;
    },

    isMatchingProductsDataLoaded() {
      return this.sourceScenarioId && !isEmpty(this.matchingProductsInfo);
    },

    scenariosSearchQuery() {
      return { 'jobs.setupProductModelling.status': jobapi.jobStatuses.finished };
    },

    isFilteredProductsToggleDisabled() {
      // filteredProductDisplayKeys will be null when there is no filtering applied or be an array with length 0
      // when there is filtering but it's excluded all products. In either case, the toggle should be disabled.
      return !size(this.filteredProductDisplayKeys);
    },

    selectedToggle() {
      return !this.isFilteredProductsToggleDisabled ? this.toggleValue : toggleOptions.allProducts;
    },

    /**
     * The productKeyDisplay values of the products to copy decisions for. Will be null when
     * the "Filtered products" toggle is not selected or the toggle is disabled.
     */
    displayKeysToCopy() {
      const useFilteredProducts =
        this.selectedToggle === toggleOptions.filteredProducts &&
        !this.isFilteredProductsToggleDisabled;

      return useFilteredProducts ? this.filteredProductDisplayKeys : null;
    },
  },

  watch: {
    isProcessing(isProcessing, wasProcessing) {
      if (!isProcessing && wasProcessing) this.closeModal();
    },
  },

  methods: {
    ...mapActions(useScenarioProductsStore, ['getMatchingProductsInfo', 'copyModellingDecisions']),

    closeModal(params) {
      this.$emit('close', params);
      // Do not clear user selections on close, leave as they are
    },

    async copyDecisions() {
      this.$emit('confirm');
      const selectedDecisions = filter(
        keys(this.selectedDecisions),
        k => this.selectedDecisions[k]
      );
      const change = await this.copyModellingDecisions({
        sourceScenarioId: this.sourceScenarioId,
        selectedDecisions,
        filteredProductDisplayKeysToCopy: this.displayKeysToCopy,
      });
      this.$emit('diff-change', change.data);
      this.closeModal();
    },

    updateSelectedScenario(scenarioId) {
      this.sourceScenarioId = scenarioId;

      // If user is clearing the scenario, discard the changes
      if (!scenarioId) this.discard();
    },

    async retrieveMatchingProductsInfo() {
      if (!this.sourceScenarioId) return;

      this.isLoading = true;

      try {
        this.matchingProductsInfo = await this.getMatchingProductsInfo(
          this.sourceScenarioId,
          this.displayKeysToCopy
        );
      } catch (err) {
        console.error(err);
      }
      this.isLoading = false;
    },

    async onScenarioIdChange(scenarioId) {
      this.updateSelectedScenario(scenarioId);
      await this.retrieveMatchingProductsInfo();
    },

    async onFilterToggleSelected(value) {
      this.toggleValue = value;
      await this.retrieveMatchingProductsInfo();
    },

    // Reset all data to defaults
    discard() {
      this.isLoading = false;
      this.sourceScenarioId = null;
      this.matchingProductsInfo = {};
      this.selectedDecisions = {
        sisterProducts: true,
        trendApplied: false,
        rateOfSaleOverride: false,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.sub-title {
  font-size: 1.2rem;
  font-weight: bold;
}

.main-container {
  overflow-y: auto;
  flex-basis: auto;
  font-size: 1.3rem;
}

:deep(.dialog-card__footer) {
  border-top-width: 0px;
}

:deep(.v-alert) {
  background: $assortment-warning-alert-background !important;
  border: 1px solid $assortment-warning-alert-border !important;
  border-radius: 0 !important;
  padding: 14px;

  &:before {
    content: none;
  }

  .v-alert__content {
    color: $assortment-text-colour;
    font-size: 1.2rem;
    line-height: 1.5rem;
  }
}
</style>
